/**
 * How every printed page says what it belongs to (Hunter, Sep 18: pages that
 * fall out of the stack have to say what they are).
 *
 * - The ingredient table, the recipe make list and every recipe page carry a
 *   repeating `<thead>` with the kitchen, the delivery days and when it was
 *   printed. Browsers repeat a table's header group on every printed page; a
 *   `<caption>` does not repeat.
 * - A named `@page ordo-packet` puts "Page N of M" in the top-right margin
 *   box. Chromium 131+ draws margin boxes; Safari ignores named pages and
 *   Firefox has no margin boxes, and both still get the repeating header.
 *   Chrome's own header and footer (date, title, URL, "N/M"), on by default,
 *   use the other margin boxes, so only top-right is ours.
 * - The document title (the PDF's file name, and Chrome's centered header)
 *   stays at or under 32 characters so it never runs into top-right.
 *
 * Pure strings, unit-tested. The page rule itself lives in `globals.css`.
 */

/** The class that binds a packet's pages to the named `@page ordo-packet`, defined in `globals.css`. */
export const ORDO_PACKET_PAGE_CLASS = 'print-page-ordo-packet'

/** Longest document title that stays clear of the top-right margin box. */
export const PRINT_TITLE_MAX_LENGTH = 32

function dayLabel(date: Date): string {
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

/** "Mon, Sep 21 to Fri, Sep 25", or "Mon, Sep 21" for one day. */
export function deliveryRangeLabel(first: Date, last: Date): string {
  const a = dayLabel(first)
  const b = dayLabel(last)
  return a === b ? a : `${a} to ${b}`
}

/**
 * "Wed, Sep 23, 10:12 AM". Built from parts: ICU versions join a date and a
 * time differently ("Sep 23 at 10:12", "Sep 23, 10:12") and newer ones put a
 * narrow no-break space before "AM".
 */
export function printedAtLabel(printedAt: Date): string {
  const time = printedAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).replace(/\u202f/g, ' ')
  return `${dayLabel(printedAt)}, ${time}`
}

/**
 * Row 1 of the shopping list's repeating header, the name the Ingredients
 * page gives its list: "Shopping list · Jeff's Over the Top Catering".
 */
export function shoppingListPrintHeading(kitchenName: string): string {
  return `Shopping list · ${kitchenName}`
}

/** Row 1 on every recipe page, the make list included: "Recipes · Jeff's Over the Top Catering". */
export function recipesPrintHeading(kitchenName: string): string {
  return `Recipes · ${kitchenName}`
}

/** Row 2: "Deliveries Mon, Sep 21 to Fri, Sep 25 · Printed Wed, Sep 23, 10:12 AM". */
export function deliveriesPrintSubheading(first: Date, last: Date, printedAt: Date): string {
  return `Deliveries ${deliveryRangeLabel(first, last)} · Printed ${printedAtLabel(printedAt)}`
}

/** The Ingredients table's row 2; the same words as every other packet's. */
export const ingredientPrintSubheading = deliveriesPrintSubheading

/** The Orders packet's name on every school sheet: "Orders · Jeff's Over the Top Catering". */
export function ordersPrintHeading(kitchenName: string): string {
  return `Orders · ${kitchenName}`
}

/** The driver's route sheet (Delivery page): "Delivery · Jeff's Over the Top Catering". */
export function deliveryPrintHeading(kitchenName: string): string {
  return `Delivery · ${kitchenName}`
}

/**
 * The identity line on a school sheet or the route sheet: the packet and
 * kitchen, the delivery day, when printed. "Orders · Jeff's Over the Top
 * Catering · Deliveries Thu, Sep 24 · Printed Thu, Sep 24, 6:05 AM". A page
 * that falls out of the stack still says whose pile it belongs to (Hunter,
 * Sep 18).
 */
export function sheetPrintSubheading(packetHeading: string, day: Date, printedAt: Date): string {
  return `${packetHeading} · ${deliveriesPrintSubheading(day, day, printedAt)}`
}

/** The packet cover's date line: "Deliveries Mon, Sep 21 to Fri, Sep 25 · Printed Wed, Sep 23, 10:12 AM". */
export const coverPrintSubheading = deliveriesPrintSubheading

/** "Sep 21–25", "Sep 28–Oct 2", "Sep 21". */
function compactRange(first: Date, last: Date): string {
  const month = (d: Date) => d.toLocaleDateString('en-US', { month: 'short' })
  const a = `${month(first)} ${first.getDate()}`
  if (first.toDateString() === last.toDateString()) {
    return a
  }
  return month(first) === month(last) ? `${a}–${last.getDate()}` : `${a}–${month(last)} ${last.getDate()}`
}

/** "Ingredients · Sep 21–25", never over {@link PRINT_TITLE_MAX_LENGTH}. */
export function shortPrintTitle(label: string, first: Date, last: Date): string {
  const title = `${label} · ${compactRange(first, last)}`
  return title.length <= PRINT_TITLE_MAX_LENGTH ? title : title.slice(0, PRINT_TITLE_MAX_LENGTH)
}

/**
 * A packet printed while a school can still change its order (PROD-1452):
 * "INCOMPLETE Orders · Sep 21–25". The word leads, so neither the length cap
 * nor a long label can drop it; when the label does not fit, the date range
 * stays and the label goes ("INCOMPLETE · Sep 28–Oct 2").
 */
export function shortIncompletePrintTitle(label: string, first: Date, last: Date): string {
  const range = compactRange(first, last)
  const title = `INCOMPLETE ${label} · ${range}`
  return title.length <= PRINT_TITLE_MAX_LENGTH ? title : `INCOMPLETE · ${range}`.slice(0, PRINT_TITLE_MAX_LENGTH)
}
